import { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { R1PrimaryButton, R1SecondaryButton } from "@/components/ui/r1-button";
import { usePortfolioComments } from "../hooks/usePortfolioComments";
import type { PortfolioComment } from "../domain/types";

interface Props {
  open: boolean;
  onClose: () => void;
  fundId: number;
  fundName: string;
  quarter: string;
}

function formatTs(iso: string): string {
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function PortfolioCommentSheet({
  open,
  onClose,
  fundId,
  fundName,
  quarter,
}: Props) {
  const { comments, isLoading, post, isPosting } = usePortfolioComments(
    fundId,
    quarter,
    open,
  );
  const [message, setMessage] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [comments]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => textareaRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [open]);

  function handleSend() {
    const trimmed = message.trim();
    if (!trimmed || isPosting) return;
    post(trimmed, {
      onSuccess: () => {
        setMessage("");
        textareaRef.current?.focus();
      },
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        hideOverlay
        className="flex flex-col p-0 w-[480px] sm:max-w-[480px]"
      >
        <SheetHeader className="border-b border-slate-200 px-6 py-4 shrink-0">
          <SheetTitle className="text-base font-semibold text-slate-800">
            Comments
          </SheetTitle>
          <p className="text-xs text-slate-500 mt-0.5">
            {fundName} · {quarter}
          </p>
        </SheetHeader>

        <div
          ref={listRef}
          className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
        >
          {isLoading ? (
            <p className="text-center text-xs text-slate-400 mt-8">Loading…</p>
          ) : comments.length === 0 ? (
            <p className="text-center text-xs text-slate-400 mt-8">
              No comments yet.
            </p>
          ) : (
            comments.map((c) => <CommentBubble key={c.id} comment={c} />)
          )}
        </div>

        <div className="border-t border-slate-200 px-4 py-3 space-y-2 shrink-0">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="New comment… (Enter to send, Shift+Enter for new line)"
            rows={3}
            className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <div className="flex justify-end gap-2">
            <R1SecondaryButton
              onPress={onClose}
              className="h-8 text-xs px-3 rounded-md"
            >
              Close
            </R1SecondaryButton>
            <R1PrimaryButton
              onPress={handleSend}
              isDisabled={!message.trim() || isPosting}
              isLoading={isPosting}
              className="h-8 text-xs px-3 rounded-md"
            >
              Send
            </R1PrimaryButton>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CommentBubble({ comment }: { comment: PortfolioComment }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
      <div className="flex items-center justify-between mb-1 gap-2">
        <span className="text-xs font-medium text-slate-700 truncate">
          {comment.user_name || comment.user_email}
        </span>
        <span className="text-[11px] text-slate-400 shrink-0">
          {formatTs(comment.created_at)}
        </span>
      </div>
      <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
        {comment.message}
      </p>
    </div>
  );
}
